import { Link } from "@tanstack/react-router";
import { MirrorTag } from "./mirror-mark";
import { ChevronRight } from "lucide-react";
import React, { type ReactElement, type ReactNode } from "react";
import { Button, cn } from "@ndd/ui";
import type { AppointmentListItem } from "../../../../shared/api-types";
import type { AppointmentListSearch } from "@/lib/appointments";
import type { AppointmentHomeSection } from "@/lib/use-appointment-agenda";
import {
  AGENDA_CARD,
  AGENDA_TIME,
  AGENDA_UTILITY_RADIUS,
  agendaRowClass,
} from "./agenda-look";
import {
  agendaNameStruck,
  AppointmentRowStatusPill,
  DraftReadyMark,
  useAgendaStatusSplit,
} from "./appointment-status";

export function AppointmentSchedule({
  sections,
  listSearch,
  upNextId,
  endedTodayIds,
  showNonDefaultStatuses,
  viewingOther,
  onStatusChange,
  onViewPast,
}: {
  sections: AppointmentHomeSection[];
  listSearch: AppointmentListSearch;
  upNextId: string | null;
  endedTodayIds: ReadonlySet<string>;
  showNonDefaultStatuses: boolean;
  /** Another RD's schedule: copy must not address the viewer as its owner. */
  viewingOther: boolean;
  onStatusChange: (checked: boolean) => void;
  /** The empty card's way to the Past view (NO-767). */
  onViewPast: () => void;
}): ReactElement {
  const statusToggle = (
    <AppointmentStatusToggle
      checked={showNonDefaultStatuses}
      onChange={onStatusChange}
    />
  );

  return (
    <AppointmentSections
      sections={sections}
      statusToggle={statusToggle}
      listSearch={listSearch}
      upNextId={upNextId}
      endedTodayIds={endedTodayIds}
      viewingOther={viewingOther}
      onViewPast={onViewPast}
    />
  );
}

/** The native status checkbox, shared by the Upcoming and Past views. */
export function AppointmentStatusToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}): ReactElement {
  return (
    <label className="flex w-fit cursor-pointer items-center gap-2 text-body text-muted-foreground">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="size-4 rounded border-border-strong accent-primary"
      />
      Show missed, cancelled, and rescheduled
    </label>
  );
}

/** NO-767: an empty Upcoming offers the way to Past. */
function UpcomingEmptyCard({
  statusToggle,
  viewingOther,
  onViewPast,
}: {
  statusToggle: ReactNode;
  viewingOther: boolean;
  onViewPast: () => void;
}): ReactElement {
  return (
    <>
      <div className="mt-8 flex justify-end">{statusToggle}</div>
      <div className="mt-3 rounded-xl border border-border bg-card px-6 py-14 text-center">
        <p className="text-sm font-medium text-foreground">No upcoming Appointments.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {viewingOther
            ? "New Appointments on this RD's Healthie calendar appear here."
            : "New Appointments on your Healthie calendar appear here."}
        </p>
        <Button
          type="button"
          variant="utility"
          size="compact"
          shape="pill"
          className={cn("mt-5 px-5 text-body text-muted-strong", AGENDA_UTILITY_RADIUS)}
          onClick={onViewPast}
        >
          View past appointments
        </Button>
      </div>
    </>
  );
}

function AppointmentSections({
  sections,
  statusToggle,
  listSearch,
  upNextId,
  endedTodayIds,
  viewingOther,
  onViewPast,
}: {
  sections: AppointmentHomeSection[];
  statusToggle: ReactNode;
  listSearch: AppointmentListSearch;
  upNextId: string | null;
  endedTodayIds: ReadonlySet<string>;
  viewingOther: boolean;
  onViewPast: () => void;
}): ReactElement {
  if (sections.length === 0) {
    return (
      <UpcomingEmptyCard
        statusToggle={statusToggle}
        viewingOther={viewingOther}
        onViewPast={onViewPast}
      />
    );
  }

  return (
    <>
      {sections.map((section, index) => (
        <section key={section.key} className="mt-7 first:mt-8">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h2 className="font-sans text-base font-bold text-foreground">
              {section.label}
            </h2>
            {index === 0 ? statusToggle : null}
          </div>
          <div className={AGENDA_CARD}>
            {section.rows.map((row) => (
              <AppointmentRow
                key={row.item.id}
                item={row.item}
                timeLabel={row.timeLabel}
                listSearch={listSearch}
                isUpNext={row.item.id === upNextId}
                hasEndedToday={endedTodayIds.has(row.item.id)}
              />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function AppointmentRow({
  item,
  timeLabel,
  listSearch,
  isUpNext,
  hasEndedToday = false,
}: {
  item: AppointmentListItem;
  timeLabel: string;
  listSearch: AppointmentListSearch;
  isUpNext: boolean;
  hasEndedToday?: boolean;
}): ReactElement {
  const split = useAgendaStatusSplit();
  const struck = agendaNameStruck(item.status, split);

  return (
    <Link
      to="/appointments/$appointmentId"
      params={{ appointmentId: item.id }}
      search={listSearch}
      className={agendaRowClass(isUpNext)}
    >
      <span
        className={cn(
          "w-[74px] shrink-0 text-body",
          AGENDA_TIME,
          isUpNext
            ? "font-semibold text-info"
            : hasEndedToday
              ? "text-subtle"
              : "text-muted-foreground"
        )}
      >
        {timeLabel}
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-sm",
          hasEndedToday
            ? "font-normal text-muted-strong"
            : "font-medium text-foreground",
          struck && "line-through"
        )}
      >
        {item.patientName || "Patient"}
      </span>
      <MirrorTag mirrorState={item.mirrorState} />
      <DraftReadyMark item={item} />
      <AppointmentRowStatusPill item={item} isUpNext={isUpNext} />
      <ChevronRight
        aria-hidden="true"
        className="size-4 shrink-0 text-sand-300 transition-colors group-hover:text-muted-foreground"
      />
    </Link>
  );
}
